import { observable, computed, action, runInAction, autorun, when, observe } from 'mobx';
import _ from 'lodash';
import Cons from '../../isomorphic/cons';
import FilterOptions from '../shared/filter-options';
import DashboardFilters from '../dashboard/dashboard-filters';
import TcFileModel from '../files/tcfile-model';

export default class ViewStore {
    @observable page = '';
    previousPage = { label: '', href: '' };
    @observable selectedTab = '';
    @observable selectedFileId = null;
    @observable selectedAgentId = null;
    @observable addFileDialogOpen = false;
    @observable fileTableViewOptions = new FilterOptions();
    @observable confirmFileDeleteDialogOpen = false;
    @observable invoiceFileDialogOpen = false;
    @observable preferredEmailDialogOpen = false;
    @observable unlinkGoogleAccountDialogOpen = false;
    @observable createTemplateDialogOpen = false;

    constructor(appstore) {
        this.appStore = appstore;
        observe(this, 'page', (change) => {
            this.previousPage.page = change.oldValue;
            const pageKey = _.findKey(Cons.pages, (page) => page === change.oldValue);
            this.previousPage.label = _.upperFirst(Cons.pagesLabels[pageKey] || pageKey);
        });
    }

    @computed
    get isLoading() {
        return this.appStore.isLoading;
    }

    @computed
    get isUserLoaded() {
        return (
            this.appStore.authStore.authData &&
            this.appStore.coordinatorDetailStore.coordinator &&
            this.appStore.coordinatorDetailStore.coordinator.coordinatorId
        );
    }

    @computed
    get currentUrl() {
        switch (this.page) {
            case '':
                return '/';
            case Cons.pages.dashBoard:
                return '#/dashboard';
            case Cons.pages.files:
                return '#/files';
            case Cons.pages.makeListing:
                return '#/files/makelisting';
            case Cons.pages.makeTransaction:
                return '#/files/maketransaction';
            case Cons.pages.fileDetail:
                return '#/filedetail/' + this.selectedFileId;
            case Cons.pages.reports:
                return '#/reports';
            case Cons.pages.coordinators:
                return '#/coordinators';
            case Cons.pages.agents:
                return '#/agents';
            case Cons.pages.agentProfile:
                return '#/agentprofile/' + this.selectedAgentId;
            case Cons.pages.salesAgents:
                return '#/sales/agents';
            case Cons.pages.emailTemplates:
                return '#/emailtemplates';
            default:
                return '#/404';
        }
    }

    @action.bound
    showAddFileDialog() {
        this.appStore.tcFileStore.duplicateFiles.clear();
        this.addFileDialogOpen = true;
    }

    @action.bound
    hideAddFileDialog() {
        this.addFileDialogOpen = false;
    }

    @action.bound
    openDashBoardPage() {
        this.page = Cons.pages.dashBoard;
        this.selectedTab = Cons.pages.dashBoard;

        when(() => this.appStore.coordinatorDetailStore.coordinator.roles, this.appStore.dashboardStore.loadData);
    }

    @action.bound
    openFilesPage() {
        this.page = Cons.pages.files;
        this.selectedTab = Cons.pages.files;

        if (this.fileTableViewOptions.filtersKP.length === 0) {
            this.setDefaultFilters();
            this.setDefaultSort();
        } else {
            this.appStore.tcFileStore.loadFiles();
        }

        this.selectedFileId = null;
        this.appStore.tcFileStore.selectedFile = new TcFileModel('new');
        this.appStore.workflowStore.timelineWorkflowStore.clearContractAcceptanceDate(); // todo refactor this up?
        this.hideInvoiceFileDialogModal();
        this.hideTemplateDialogModal();
    }

    @action
    setDefaultFilters() {
        this.setFilepageFilter({
            label: 'Pending',
            type: 'status',
            filter: { where: { status: Cons.fileStatuses.Pending } },
        });
        this.setFilepageFilter({
            label: 'Incomplete',
            type: 'status',
            filter: { where: { status: Cons.fileStatuses.Incomplete } },
        });
    }

    @action
    setDefaultSort() {
        this.fileTableViewOptions.sortName.replace(['estimatedCloseDate', 'status']);
        this.fileTableViewOptions.sortOrder.replace(['asc', 'asc']);
    }

    @action
    updateFileTableSort(name, order) {
        this.fileTableViewOptions.setSort(name, order);
    }

    @action
    openPreviousPage() {
        this.page = this.previousPage.page;
    }

    @action
    openDefaultPage() {
        when(
            () => this.appStore.coordinatorDetailStore.coordinator.roles,
            () => {
                switch (this.appStore.coordinatorDetailStore.coordinator.role) {
                    case Cons.roles.tc:
                        this.openFilesPage();
                        break;
                    case Cons.roles.admin:
                        this.openFilesPage();
                        break;
                    case Cons.roles.superadmin:
                        this.openFilesPage();
                        break;
                    case Cons.roles.sales:
                        this.openSalesPage();
                        break;
                    default:
                        this.openFilesPage();
                        break;
                }
            }
        );
    }

    @action.bound
    openFileDetailPageById(fileId) {
        this.page = Cons.pages.fileDetail;
        this.selectedTab = Cons.pages.files;

        this.appStore.coordinatorStore.loadCoordinators();
        this.appStore.emailTemplateStore.loadTemplates();

        this.selectedFileId = fileId;
        this.appStore.tcFileStore.loadFile(this.selectedFileId).then(() => {
            this.appStore.tcFileStore.loadFileContacts();
            this.appStore.workflowStore.loadWorkflow();
            this.appStore.workflowStore.loadTimelineEvents();
        });

        this.appStore.featureFlagStore.loadFeatures();
    }

    @action.bound
    openFileDetailPageForNew(newFile) {
        this.page = Cons.pages.fileDetail;
        if (this.selectedTab !== Cons.pages.files) {
            this.openFilesPage();
        }
        this.appStore.coordinatorStore.loadCoordinators();
        this.appStore.emailTemplateStore.loadTemplates();
        this.appStore.workflowStore.resetWorkflowState();
        this.selectedFileId = newFile.tcFileId;
    }

    @action.bound
    openReportsPage() {
        this.page = Cons.pages.reports;
        this.selectedTab = Cons.pages.reports;

        this.selectedFileId = null;
    }

    @action.bound
    openCoordinatorsPage() {
        this.page = Cons.pages.coordinators;
        this.selectedTab = Cons.pages.coordinators;

        this.selectedFileId = null;
    }

    @action.bound
    openAgentsPage() {
        this.page = Cons.pages.agents;
        this.selectedTab = Cons.pages.agents;
        this.selectedFileId = null;
        this.appStore.tcFileStore.selectedFile = null;
        when(
            () => this.appStore.coordinatorDetailStore.coordinator.coordinatorId,
            () => {
                runInAction(() => {
                    this.appStore.agentStore.loadWatchAgents();
                    this.appStore.agentStore.loadFileAgents();
                });
            }
        );
    }

    @action.bound
    openAgentProfilePageById(selectedTab, agentId, subscriberId, subscriberName) {
        if (this.appStore.agentStore.combinedAgents.length === 0) {
            this.openAgentsPage();
        } else {
            this.page = Cons.pages.agentProfile;
            this.selectedTab = selectedTab;

            this.appStore.coordinatorStore.loadCoordinators();
            this.selectedAgentId = agentId;
            this.appStore.agentProfileStore.loadAgentProfile(this.selectedAgentId, subscriberId, subscriberName);
        }
    }

    @action.bound
    openEmailTemplatesPage() {
        this.page = Cons.pages.emailTemplates;
        this.selectedTab = Cons.pages.emailTemplates;
        this.selectedFileId = null;
        this.appStore.emailTemplateStore.loadTemplates();
    }

    @action.bound
    handleAuth(query) {
        appStore.authStore.setAuthData(query);
    }

    loadFileRunner = autorun(() => {
        if (this.fileTableViewOptions.filter && this.appStore) {
            this.appStore.tcFileStore.loadFiles();
        }
    });

    @action
    removeFilepageFilter(key) {
        this.fileTableViewOptions.filters.delete(key);
    }

    @action
    setFilepageFilter(filter) {
        this.fileTableViewOptions.filters.set(`${filter.type} : ${filter.label}`, filter);
    }

    @action
    openFilePageWithFilter(filter) {
        if (filter.type === 'dashboard') {
            this.fileTableViewOptions.filters.clear();
        }
        this.fileTableViewOptions.filters.set(`${filter.type} : ${filter.label}`, filter);
        this.openFilesPage();
    }

    @action.bound
    showConfirmFileDeleteModal() {
        this.confirmFileDeleteDialogOpen = true;
    }

    @action.bound
    hideConfirmFileDeleteModal() {
        this.confirmFileDeleteDialogOpen = false;
    }

    @action.bound
    openAddListingToPrimePage(typeOfSale) {
        this.appStore.tcFileStore.clearLookupAgentOffices();
        this.appStore.tcFileStore.clearLookupAgentPropertyTypes();
        this.appStore.tcFileStore.newListing = { fileType: Cons.fileTypes.listing, typeOfSale: typeOfSale };
        this.page = Cons.pages.makeListing;
        this.selectedTab = Cons.pages.files;
        this.selectedFileId = null;
        this.newListing = null;
        this.hideAddFileDialog();
        this.appStore.featureFlagStore.loadFeatures(); // cache this and put it someplace more central?
    }

    @action.bound
    openAddTransactionToPrimePage(typeOfSale) {
        this.appStore.tcFileStore.clearLookupAgentOffices();
        this.appStore.tcFileStore.clearLookupAgentPropertyTypes();
        this.appStore.tcFileStore.newTransaction = { fileType: Cons.fileTypes.transaction, typeOfSale: typeOfSale };
        this.page = Cons.pages.makeTransaction;
        this.selectedTab = Cons.pages.files;
        this.selectedFileId = null;
        this.hideAddFileDialog();
        this.appStore.featureFlagStore.loadFeatures(); // cache this and put it someplace more central?
    }

    @action.bound
    openSalesPage() {
        this.page = Cons.pages.salesAgents;
        this.selectedTab = Cons.pages.salesAgents;
        this.selectedFileId = null;
        this.newListing = null;
        when(
            () => this.appStore.coordinatorDetailStore.coordinator.coordinatorId,
            () => {
                runInAction(() => {
                    this.appStore.agentStore.loadWatchAgents();
                    this.appStore.agentStore.loadFileAgents();
                });
            }
        );
        this.hideAddFileDialog();
    }

    @action.bound
    showInvoiceFileDialogModal() {
        this.invoiceFileDialogOpen = true;
    }

    @action.bound
    hideInvoiceFileDialogModal() {
        this.invoiceFileDialogOpen = false;
    }

    @action.bound
    createTemplateDialogModal() {
        this.createTemplateDialogOpen = true;
    }

    @action.bound
    hideTemplateDialogModal() {
        this.createTemplateDialogOpen = false;
    }

    @action.bound
    showPreferredEmailDialog() {
        this.preferredEmailDialogOpen = true;
    }

    @action.bound
    hidePreferredEmailDialog() {
        this.preferredEmailDialogOpen = false;
    }

    @action.bound
    showUnlinkGoogleAccountModal() {
        this.unlinkGoogleAccountDialogOpen = true;
    }

    @action.bound
    hideUnlinkGoogleAccountModal() {
        this.unlinkGoogleAccountDialogOpen = false;
    }
}



// WEBPACK FOOTER //
// ./src/app-main/view-store.js